/**
 * WhatsAppModal — prepare + open WhatsApp for a lead.
 * Renders a template with {{variables}}, shows the E.164-normalised phone,
 * lets the user edit the message, then: Open WhatsApp (wa.me) · Copy ·
 * Save as Template · Send via Twilio (existing API) · Cancel.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageCircle, Loader2, X, Send, ExternalLink, Copy, Save, AlertTriangle,
} from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WhatsAppModal({ open, onClose, lead }) {
  const { t, lang } = useLang();
  const isPt = lang === "pt";
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [body, setBody] = useState("");
  const [prep, setPrep] = useState(null);       // { ok, status, phone, wa_link }
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const prepare = async (tplId, msgOverride) => {
    if (!lead?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads/${lead.id}/wa/prepare`, {
        template_id: tplId || "", message: msgOverride || "",
      });
      setPrep(data);
      if (data.message !== undefined && !msgOverride) setBody(data.message || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "prepare failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open || !lead) return;
    setResult(null); setTemplateId(""); setBody(""); setPrep(null);
    axios.get(`${API}/wa-templates`)
      .then(({ data }) => setTemplates((data.templates || []).filter((x) => x.active !== false)))
      .catch(() => setTemplates([]));
    prepare("");
  }, [open, lead?.id]); // eslint-disable-line

  if (!open || !lead) return null;

  const phoneShown = prep?.phone?.normalized || lead.phone_normalized || lead.phone || "—";
  const phoneValid = prep ? prep.status === "valid" : !!lead.phone;
  const optedOut = prep?.status === "opted_out";

  const waLink = () => {
    const digits = (prep?.phone?.normalized || "").replace(/\D/g, "");
    if (!digits) return null;
    return `https://wa.me/${digits}?text=${encodeURIComponent(body)}`;
  };

  const openWhatsApp = async () => {
    const link = waLink();
    if (!link) return;
    window.open(link, "_blank", "noopener");
    try {
      await axios.post(`${API}/leads/${lead.id}/wa/log`, {
        event: "opened", message_preview: body.slice(0, 140),
      });
    } catch (_e) {}
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(body);
      toast.success(isPt ? "Mensagem copiada" : "Message copied");
      await axios.post(`${API}/leads/${lead.id}/wa/log`, {
        event: "copied", message_preview: body.slice(0, 140),
      }).catch(() => {});
    } catch (_e) {
      toast.error(isPt ? "Falha ao copiar" : "Copy failed");
    }
  };

  const saveTemplate = async () => {
    const name = window.prompt(isPt ? "Nome do template:" : "Template name:");
    if (!name) return;
    try {
      await axios.post(`${API}/wa-templates`, {
        name, message: body, language: lang, category: "First Contact",
      });
      toast.success(isPt ? "Template salvo" : "Template saved");
      const { data } = await axios.get(`${API}/wa-templates`);
      setTemplates((data.templates || []).filter((x) => x.active !== false));
    } catch (_e) { toast.error(isPt ? "Falha ao salvar template" : "Save failed"); }
  };

  const sendTwilio = async () => {
    const phone = prep?.phone?.normalized || lead.phone;
    if (!phone) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/whatsapp/send`, {
        to: phone, body, lead_id: lead.id,
      });
      setResult(data);
      if (data.ok) {
        toast.success(isPt ? "Mensagem enviada via Twilio" : "Sent via Twilio");
        axios.post(`${API}/leads/${lead.id}/wa/log`, {
          event: "sent", message_preview: body.slice(0, 140),
        }).catch(() => {});
      } else toast.warning(data.reason || "failed");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "failed";
      toast.error(detail);
      setResult({ ok: false, reason: detail });
    } finally { setSending(false); }
  };

  const onSelectTemplate = (id) => {
    setTemplateId(id);
    prepare(id);
  };

  return (
    <div data-testid="whatsapp-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="al-card max-w-[580px] w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#25D366]" />
            <div>
              <h2 className="text-[16px] font-bold text-[#111827]">
                {isPt ? "Enviar mensagem no WhatsApp" : "Send WhatsApp Message"}
              </h2>
              <p className="text-[12px] text-[#6B7280] mt-0.5 flex items-center gap-1.5 flex-wrap">
                <span className="font-semibold">{lead.name}</span> ·
                <span data-testid="wa-phone-display">{phoneShown}</span>
                {phoneValid ? (
                  <span data-testid="wa-phone-valid" className="inline-flex items-center gap-1 text-[10px] font-bold text-[#25D366]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#25D366]" />
                    {isPt ? "WhatsApp disponível" : "WhatsApp available"}
                  </span>
                ) : (
                  <span data-testid="wa-phone-invalid" className="inline-flex items-center gap-1 text-[10px] font-bold text-[#DC2626]">
                    <AlertTriangle className="w-3 h-3" />
                    {isPt ? "Telefone inválido ou incompleto" : "The phone number is invalid or incomplete"}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111827]" data-testid="wa-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {optedOut && (
          <div data-testid="wa-optout-warning" className="mb-3 p-3 rounded-lg bg-[#FEE2E2] text-[#991B1B] text-[12px]">
            {isPt
              ? "Este contato optou por não receber mensagens (opt-out / Do Not Contact)."
              : "This contact is opted out / marked Do Not Contact."}
          </div>
        )}

        <div className="space-y-3">
          {templates.length > 0 && (
            <div>
              <label className="block text-[12px] font-semibold text-[#374151] mb-1">
                {isPt ? "Template" : "Template"}
              </label>
              <select
                data-testid="wa-template-select"
                value={templateId}
                onChange={(e) => onSelectTemplate(e.target.value)}
                className="w-full px-3 h-9 border border-[#E5E7EB] rounded-lg text-[13px] bg-white"
              >
                <option value="">{isPt ? "— Padrão —" : "— Default —"}</option>
                {templates.map((tp) => (
                  <option key={tp.id} value={tp.id}>{tp.name} ({tp.category})</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-[12px] font-semibold text-[#374151] mb-1">
              {isPt ? "Mensagem" : "Message"}
            </label>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-[#25D366]" />
              </div>
            ) : (
              <textarea
                data-testid="wa-body-input"
                rows={6}
                maxLength={1500}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-[13px] bg-white focus:outline-none focus:border-[#25D366]"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            )}
            <div className="text-[10.5px] text-[#9CA3AF] mt-1 text-right">{body.length}/1500</div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="wa-open-btn"
              disabled={!phoneValid || !body.trim() || optedOut}
              onClick={openWhatsApp}
              title={!phoneValid ? (isPt ? "Adicione um telefone válido para ativar o WhatsApp." : "Add a valid phone number to enable WhatsApp.") : ""}
              className="flex-1 min-w-[150px] inline-flex items-center justify-center gap-2 h-10 rounded-lg text-[13px] font-semibold text-white disabled:opacity-50"
              style={{ background: "#25D366" }}
            >
              <ExternalLink className="w-4 h-4" />
              {isPt ? "Abrir WhatsApp" : "Open WhatsApp"}
            </button>
            <button type="button" data-testid="wa-copy-btn" onClick={copyMessage} disabled={!body.trim()}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-[#E5E7EB] text-[12.5px] font-semibold text-[#374151] hover:border-[#25D366] disabled:opacity-50">
              <Copy className="w-3.5 h-3.5" /> {isPt ? "Copiar" : "Copy Message"}
            </button>
            <button type="button" data-testid="wa-save-template-btn" onClick={saveTemplate} disabled={!body.trim()}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-[#E5E7EB] text-[12.5px] font-semibold text-[#374151] hover:border-[#3B82F6] disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {isPt ? "Salvar template" : "Save as Template"}
            </button>
          </div>

          <button
            type="button"
            data-testid="wa-send-btn"
            disabled={sending || !body.trim() || !phoneValid || optedOut}
            onClick={sendTwilio}
            className="flex items-center justify-center gap-2 w-full h-9 rounded-lg text-[12.5px] font-semibold disabled:opacity-50 border border-[#25D366] text-[#128C7E] hover:bg-[#25D36610]"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {isPt ? "Enviar via Twilio (API)" : "Send via Twilio (API)"}
          </button>
        </div>

        {result && (
          <div
            data-testid="wa-result"
            className={"mt-4 p-3 rounded-lg text-[12px] " + (result.ok ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEE2E2] text-[#991B1B]")}
          >
            {result.ok ? (
              <>✅ {t("wa.queued", { sid: result.sid?.slice(-8), status: result.status })}</>
            ) : (
              <>
                ❌ {result.reason}
                {result.hint && <div className="mt-2 text-[11px] text-[#7F1D1D] italic">💡 {result.hint}</div>}
              </>
            )}
          </div>
        )}

        <p className="mt-3 text-[10.5px] text-[#9CA3AF] italic">{t("wa.disclaimer")}</p>
      </div>
    </div>
  );
}
